import type { KeyPair } from "@foreman/crypto";
import type { InboxEvent, PowerState } from "@foreman/protocol";
import { WAKE_ATTEMPT_LIMIT, type WakeStatus, type WakeSupport } from "./power";
import { ComputerConnection, inboxCounts, type ConnectionOptions, type ConnState } from "./connection";
import type { ComputersStore, ComputerRecord } from "./computers";

export interface ComputerSummary {
  computer: ComputerRecord;
  state: ConnState;
  /** Open approvals and questions on this computer (for a badge). */
  needsYou: number;
  /** Sessions running on this computer right now (as far as the last list we got says). */
  running: number;
  /** What the computer last said about its power (null: not yet, or an older Brocoded). */
  power: PowerState | null;
  /** A wake attempt in progress or just failed. */
  wake: WakeStatus | undefined;
  /** Whether a wake packet could be sent for this computer, and if not what is missing. */
  wakeSupport: WakeSupport;
}

/** After a wake packet is sent, the computer has this long to come back online. */
export const WAKE_WAIT_MS = 90_000;
/** The tries counted against the limit forget themselves after this long. */
const WAKE_WINDOW_MS = 10 * 60_000;

/** One inbox card together with the computer it belongs to. */
export interface InboxItem {
  computer: ComputerRecord;
  event: InboxEvent;
}

/**
 * One live connection per paired computer, so the computers list can show real status and
 * actions like "remove" know whether the computer is reachable. Follows the computers store:
 * pairing a computer starts its connection, removing one stops it.
 */
export class Hub {
  private readonly conns = new Map<string, { conn: ComputerConnection; off: () => void }>();
  private summaries: ComputerSummary[] = [];
  private items: InboxItem[] = [];
  private readonly listeners = new Set<() => void>();
  private offStore: (() => void) | null = null;
  private readonly wakes = new Map<string, WakeStatus & { timer?: ReturnType<typeof setTimeout>; poke?: ReturnType<typeof setInterval> }>();
  private readonly wakeTries = new Map<string, number[]>();

  constructor(
    private readonly store: ComputersStore,
    private readonly connOpts: Omit<ConnectionOptions, "keys"> & { keys: KeyPair },
  ) {}

  start(): void {
    this.sync();
    this.offStore = this.store.subscribe(() => this.sync());
  }

  stop(): void {
    this.offStore?.();
    for (const { conn, off } of this.conns.values()) {
      off();
      conn.stop();
    }
    this.conns.clear();
  }

  get(id: string): ComputerConnection | undefined {
    return this.conns.get(id)?.conn;
  }

  private accountToken: string | null = null;
  private background = false;
  /** The phone signed in (or out): every computer, now and later, is told so it can link itself to the account. */
  setAccountToken(token: string | null): void {
    this.accountToken = token;
    for (const { conn } of this.conns.values()) conn.setAccountToken(token);
  }

  /** Sign every connected computer out of the account this phone is signing out of. */
  async detachAll(token: string): Promise<void> {
    await Promise.allSettled([...this.conns.values()].map(({ conn }) => conn.detachAccount(token)));
  }

  /**
   * Signing out: sign every connected computer out of the account (when there is a token), remove this phone from each computer's
   * paired phones, and forget the computers here. The computers keep running and keep their sessions; pairing again is a QR scan.
   */
  async forgetAll(token: string | null): Promise<void> {
    for (const rec of this.store.list()) {
      const conn = this.conns.get(rec.id)?.conn;
      if (conn) {
        if (token) await conn.detachAccount(token).catch(() => undefined);
        await conn.removeDevice(rec.deviceId).catch(() => undefined);
      }
      this.store.remove(rec.id);
    }
  }

  /** The app went to the background (or came back): quiet links are checked less often while it is out of sight. */
  setBackground(background: boolean): void {
    this.background = background;
    for (const { conn } of this.conns.values()) conn.setBackground(background);
  }

  /**
   * Resolves true once this computer reports that this phone is signed in (a plan above guest), false after `ms`. Used after a
   * sign-in so the action that asked for it can run again as soon as the computer knows.
   */
  whenSignedIn(id: string, ms = 8000): Promise<boolean> {
    const conn = this.conns.get(id)?.conn;
    if (!conn) return Promise.resolve(false);
    const ok = () => {
      const p = conn.getSnapshot().account?.plan;
      return conn.getSnapshot().state === "ready" && p !== undefined && p !== "guest";
    };
    if (ok()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const done = (v: boolean) => {
        off();
        clearTimeout(timer);
        resolve(v);
      };
      const off = conn.subscribe(() => ok() && done(true));
      const timer = setTimeout(() => done(false), ms);
    });
  }

  /** Reconnect every computer now (app returned to the foreground, or the network came back). */
  reconnectAll(): void {
    for (const { conn } of this.conns.values()) conn.reconnectNow();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getSnapshot = (): ComputerSummary[] => this.summaries;
  /** Every computer's inbox together, newest first. Same array until something changes. */
  getInbox = (): InboxItem[] => this.items;

  private sync(): void {
    const wanted = new Map(this.store.list().map((c) => [c.id, c]));
    for (const [id, entry] of [...this.conns]) {
      if (!wanted.has(id)) {
        entry.off();
        entry.conn.stop();
        this.conns.delete(id);
        this.endWake(id, false);
        this.connOpts.power?.forget(id);
      }
    }
    for (const c of wanted.values()) {
      const existing = this.conns.get(c.id);
      // Re-pairing replaces the record. Even when the device id and relay are unchanged (the same phone pairing
      // again after being removed), the old connection is permanently "unauthorized", so it must be restarted.
      // Renaming changes neither of these, so it does not reconnect.
      const prev = existing?.conn.computer;
      if (existing && prev && (prev.deviceId !== c.deviceId || prev.relayUrl !== c.relayUrl || prev.pairedAt !== c.pairedAt)) {
        existing.off();
        existing.conn.stop();
        this.conns.delete(c.id);
      }
      if (!this.conns.has(c.id)) {
        const conn = new ComputerConnection(c, this.connOpts);
        const off = conn.subscribe(() => this.recompute());
        this.conns.set(c.id, { conn, off });
        conn.setAccountToken(this.accountToken);
        conn.setBackground(this.background);
        conn.start();
      }
    }
    this.recompute();
  }

  /** Is a wake possible for this computer? "available" needs its address (learned while it was online) and another of your computers online to send the packet. */
  wakeSupportFor(id: string): WakeSupport {
    const target = this.connOpts.power?.get(id).wake;
    if (!target) return "unknown";
    if (target.enabled === false) return "needs-setup";
    return this.helperFor(id) ? "available" : "needs-helper";
  }

  private helperFor(id: string): ComputerConnection | undefined {
    for (const [other, { conn }] of this.conns) {
      const snap = conn.getSnapshot();
      if (other !== id && snap.state === "ready" && !snap.powerUnsupported) return conn;
    }
    return undefined;
  }

  /**
   * Try to wake a computer that is offline. The packet goes out through another of the person's computers on the same network;
   * the computer only counts as back when it actually reconnects. At most a few tries every ten minutes.
   */
  async wake(id: string): Promise<void> {
    const cur = this.wakes.get(id);
    if (cur && cur.phase !== "failed") return;
    const target = this.connOpts.power?.get(id).wake;
    const helper = this.helperFor(id);
    const now = Date.now();
    const tries = (this.wakeTries.get(id) ?? []).filter((t) => now - t < WAKE_WINDOW_MS);
    if (tries.length >= WAKE_ATTEMPT_LIMIT) return this.setWake(id, { phase: "failed", attempts: tries.length, since: now, reason: "limit" });
    if (!target || !helper) return this.setWake(id, { phase: "failed", attempts: tries.length, since: now, reason: "not_sent", message: !target ? "This phone doesn't know where to send it yet. Open the computer once while it is on." : "Another of your computers must be online on the same network." });
    tries.push(now);
    this.wakeTries.set(id, tries);
    this.setWake(id, { phase: "sending", attempts: tries.length, since: now });
    const sent = await helper.sendWake(target.mac, target.broadcast);
    if (this.conns.get(id)?.conn.getSnapshot().state === "ready") return this.endWake(id);
    if (!sent.ok) return this.setWake(id, { phase: "failed", attempts: tries.length, since: now, reason: "not_sent", ...(sent.message ? { message: sent.message } : {}) });
    const status: WakeStatus & { timer?: ReturnType<typeof setTimeout>; poke?: ReturnType<typeof setInterval> } = { phase: "waking", attempts: tries.length, since: now };
    // a computer that just woke should be noticed quickly, not after a long reconnect delay
    status.poke = setInterval(() => this.conns.get(id)?.conn.reconnectNow(), 8_000);
    status.timer = setTimeout(() => {
      this.endWake(id, false);
      this.setWake(id, { phase: "failed", attempts: tries.length, since: now, reason: "timeout" });
    }, this.connOpts.wakeWaitMs ?? WAKE_WAIT_MS);
    this.setWake(id, status);
    this.conns.get(id)?.conn.reconnectNow();
  }

  /** Dismiss a failed wake message. */
  clearWake(id: string): void {
    if (this.wakes.get(id)?.phase === "failed") this.endWake(id);
  }

  private setWake(id: string, status: WakeStatus & { timer?: ReturnType<typeof setTimeout>; poke?: ReturnType<typeof setInterval> }): void {
    this.wakes.set(id, status);
    this.recompute();
  }

  private endWake(id: string, recompute = true): void {
    const w = this.wakes.get(id);
    if (w?.timer) clearTimeout(w.timer);
    if (w?.poke) clearInterval(w.poke);
    this.wakes.delete(id);
    if (recompute) this.recompute();
  }

  private recompute(): void {
    // a computer that is connected again is awake: that, and only that, ends a wake
    for (const [id, w] of [...this.wakes]) if (w.phase !== "failed" && this.conns.get(id)?.conn.getSnapshot().state === "ready") this.endWake(id, false);
    const computers = this.store.list();
    const snaps = computers.map((computer) => ({ computer, snap: this.conns.get(computer.id)?.conn.getSnapshot() }));
    const next: ComputerSummary[] = snaps.map(({ computer, snap }) => ({
      computer,
      state: snap?.state ?? ("connecting" as ConnState),
      needsYou: snap ? inboxCounts(snap.inbox).needsYou : 0,
      running: snap ? snap.sessions.filter((x) => x.status === "running").length : 0,
      power: snap?.power ?? null,
      wake: this.wakes.get(computer.id),
      wakeSupport: this.wakeSupportFor(computer.id),
    }));
    const nextItems: InboxItem[] = snaps
      .flatMap(({ computer, snap }) => (snap?.inbox ?? []).map((event) => ({ computer, event })))
      .sort((a, b) => b.event.createdAt - a.event.createdAt);

    const sameSummaries =
      next.length === this.summaries.length &&
      next.every((n, i) => n.computer === this.summaries[i]!.computer && n.state === this.summaries[i]!.state && n.needsYou === this.summaries[i]!.needsYou && n.running === this.summaries[i]!.running && n.power === this.summaries[i]!.power && n.wake === this.summaries[i]!.wake && n.wakeSupport === this.summaries[i]!.wakeSupport);
    const sameItems = nextItems.length === this.items.length && nextItems.every((n, i) => n.event === this.items[i]!.event && n.computer === this.items[i]!.computer);
    if (sameSummaries && sameItems) return;
    if (!sameSummaries) this.summaries = next;
    if (!sameItems) this.items = nextItems;
    for (const l of [...this.listeners]) l();
  }
}
